


















































































































































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import ClientOnly from 'vue-client-only';
import { MountingPortal } from 'portal-vue';
import Heading from '@components/base/Heading.vue';
import RichText from '@components/003-RichText/RichText.vue';
import ImageComponent from '@components/001-ImageComponent/ImageComponent.vue';
import ToggleSwitch from '@components/040-ToggleSwitch/ToggleSwitch.vue';
import ToggleCircle from '@components/041-ToggleCircle/ToggleCircle.vue';
import ToggleScale from '@components/042-ToggleScale/ToggleScale.vue';
import DialRadioGroup from '@components/043-DialRadioGroup/DialRadioGroup.vue';
import IProductPack from '@frontend/types/base/IProductPack';
import IProductPiece from '@frontend/types/base/IProductPiece';
import IImage from '@frontend/types/base/IImage';
import ILink from '@frontend/types/base/ILink';
import LinkComponent from '@components/base/LinkComponent.vue';
import IconTextLink from '@components/005-IconTextLink/IconTextLink.vue';
import BaseSlider, { IBaseSliderOptions } from '@components/base/BaseSlider';
import { IProductPieceActiveFilters, IProductPiecePossibleFilters } from '@frontend/types/base/IProductPieceFilters';
import ProductPieceTaste from '@frontend/types/base/ProductPieceTaste';
import ProductPieceConsistency from '@frontend/types/base/ProductPieceConsistency';
import NamedColors from '@frontend/types/base/NamedColors';
import { createBackgroundColorStylesFor } from '@frontend/functions/colorStyles';
import gsap from 'gsap';
import { getArrayDiff } from '@frontend/utils/Array';
import { pushToDataLayer } from '@frontend/utils/TrackingUtil';
import FilterBlock from './components/FilterBlock.vue';
import InspirationOverlay from './components/InspirationOverlay.vue';

@Component({
	hydration: true,
	componentId: 'M018',
	components: {
		LinkComponent,
		IconTextLink,
		Heading,
		RichText,
		ImageComponent,
		FilterBlock,
		ToggleSwitch,
		ToggleCircle,
		ToggleScale,
		DialRadioGroup,
		InspirationOverlay,
		ClientOnly,
		MountingPortal
	}
})
export default class InspirationMachine extends Vue {
	@Prop(String)
	protected readonly headline!: string;

	@Prop({ type: Number, default: 2 })
	protected readonly headlineLevel?: number;

	@Prop(String)
	protected readonly copy!: string;

	@Prop(Object)
	protected readonly image?: IImage;

	@Prop(Boolean)
	protected readonly usePagination?: boolean;

	@Prop(Object)
	protected readonly imagePortrait?: IImage;

	@Prop(Object)
	protected readonly possibleFilters!: IProductPiecePossibleFilters;

	@Prop(Object)
	protected readonly activeFilters!: IProductPieceActiveFilters;

	@Prop(Array)
	protected readonly productPieces!: IProductPiece[];

	@Prop(Array)
	protected readonly products!: IProductPack[];

	@Prop(Array)
	protected readonly surpriseTeaser!: IInspirationMachineTeaser[];

	@Prop(Array)
	protected readonly errorTeaser!: IInspirationMachineTeaser[];

	@Prop(Object)
	protected readonly pickAndMixLink?: ILink;

	protected layoutUtilPromise: any;

	protected LayoutUtil: any;

	protected focusLockPromise: any;

	protected FocusLock: any;

	protected baseSliderPromise?: any;

	protected BaseSlider?: any;

	protected currentPieceId: string | null = null;

	protected surpriseTeaserIndex = 0;

	protected errorTeaserIndex = 0;

	protected shuffledSurpriseTeaser = this.shuffleArray(this.surpriseTeaser);

	protected shuffledErrorTeaser = this.shuffleArray(this.errorTeaser);

	protected filterSlider?: BaseSlider;

	protected queryStringApplied: boolean = false;

	protected openerElement?: Element | null;

	protected hasResultsInViewport: boolean = false;

	protected itemsPerPaginationSet = 15; // S:6, M:9, L:12, XL:15

	protected paginationIndex = 1;

	protected sliderIntroFinished = false;

	protected sliderIntroAnimation!: GSAPTimeline;

	protected readonly tasteColorMap = {
		sweet: 'red',
		salty: 'yellow',
		sour: 'green'
	};

	protected filterState: IProductPieceActiveFilters = { ...this.matchWithPossibleFilters(this.activeFilters) };

	/**
	 * Copy of filter state to allow deep watcher to receive old filter state instead of the  new state and old state parametes being equal
	 * @protected
	 */
	protected get computedFilterState(): IProductPieceActiveFilters {
		return JSON.parse(JSON.stringify(this.filterState));
	}

	protected matchWithPossibleFilters(activeFilters: IProductPieceActiveFilters): IProductPieceActiveFilters {
		const adjustedActiveFilters = { ...activeFilters };

		if (this.possibleFilters.taste.length) {
			adjustedActiveFilters.taste = activeFilters.taste.filter(taste => {
				return this.possibleFilters.taste && this.possibleFilters.taste.includes(taste);
			});
		}

		if (!this.possibleFilters.consistency.includes(activeFilters.consistency as ProductPieceConsistency)) {
			adjustedActiveFilters.consistency = 'all';
		}

		if (!this.possibleFilters.elasticity) {
			adjustedActiveFilters.elasticity = null;
		}

		if (!this.possibleFilters.volume) {
			adjustedActiveFilters.volume = null;
		}

		if (this.possibleFilters.color.length) {
			adjustedActiveFilters.color = activeFilters.color.filter(color => {
				return this.possibleFilters.color && this.possibleFilters.color.includes(color);
			});
		}

		return adjustedActiveFilters;
	}

	protected mounted(): void {
		this.layoutUtilPromise = this.loadLayoutUtil();
		this.focusLockPromise = this.loadFocusLock();
		this.baseSliderPromise = this.loadBaseSlider();
		this.initMql();
		this.initObserver();

		// Explicitly create the color styles for the product pieces
		createBackgroundColorStylesFor(
			this.productPieces.map((productPiece: IProductPiece) => productPiece.primaryBackgroundColor)
		);

		if (window.location.search) {
			this.applyFiltersFromQueryString(window.location.search);
		} else {
			this.queryStringApplied = true;
		}

		gsap.config({ nullTargetWarn: false });
	}

	@Watch('computedFilterState', { deep: true })
	protected onFilterChange(
		newFilterState: IProductPieceActiveFilters,
		oldFilterState: IProductPieceActiveFilters
	): void {
		if (this.queryStringApplied) {
			window.history.replaceState({}, '', this.generateQueryStringFromFilters());
			this.cycleTeasers();
			this.revealResults();
		}

		this.pushFilterStateChangesToDataLayer(newFilterState, oldFilterState);
	}

	private pushFilterStateChangesToDataLayer(
		newFilterState: IProductPieceActiveFilters,
		oldFilterState: IProductPieceActiveFilters
	): void {
		const push = (type: string, value: string): void => {
			pushToDataLayer({
				event: 'ux-inspiration-filter',
				// eslint-disable-next-line @typescript-eslint/camelcase
				filer_type: type,
				// eslint-disable-next-line @typescript-eslint/camelcase
				filter_value: value
			});
		};

		const { taste, consistency, elasticity, volume, color } = newFilterState;

		if (getArrayDiff(newFilterState.taste, oldFilterState.taste).length) {
			push('taste', taste.length ? taste.sort().join(',') : 'all');
		}

		if (getArrayDiff(newFilterState.color, oldFilterState.color).length) {
			push('color', color.length ? color.sort().join(',') : 'all');
		}

		if (newFilterState.consistency !== oldFilterState.consistency) {
			push('consistency', consistency === 'all' ? 'all' : consistency);
		}

		if (newFilterState.elasticity !== oldFilterState.elasticity) {
			let value = 'all';

			if (elasticity === true) {
				value = 'elastic';
			} else if (elasticity === false) {
				value = 'nonElastic';
			}
			push('elasticity', value);
		}

		if (newFilterState.volume !== oldFilterState.volume) {
			let value = 'all';

			if (volume === true) {
				value = 'loud';
			} else if (volume === false) {
				value = 'silent';
			}
			push('volume', value);
		}
	}

	protected async loadLayoutUtil(): Promise<void> {
		this.LayoutUtil = (await import('@frontend/utils/LayoutUtil')).default;
	}

	protected async loadFocusLock(): Promise<void> {
		this.FocusLock = (await import('dom-focus-lock')).default;
	}

	protected async loadBaseSlider(): Promise<void> {
		this.BaseSlider = (await import('@components/base/BaseSlider')).default;
	}

	protected async applyFiltersFromQueryString(url: string): Promise<void> {
		await this.layoutUtilPromise;
		this.filterState = {
			...this.generateFiltersFromQueryString(url)
		};

		this.$nextTick(() => {
			this.queryStringApplied = true;
		});
	}

	// ---------------------------------------------------------------------- filters

	protected get filteredProductPieces(): IProductPiece[] {
		return this.productPieces
			.filter(this.filterTaste)
			.filter(this.filterConsistency)
			.filter(this.filterVolume)
			.filter(this.filterElasticity)
			.filter(this.filterColor);
	}

	protected get hasFilteredResults(): boolean {
		return this.filteredProductPieces.length > 0;
	}

	protected get shuffledProductPieces(): IProductPiece[] {
		return this.shuffleArray(this.filteredProductPieces);
	}

	protected get paginatedProductPieces(): IProductPiece[] {
		return this.shuffledProductPieces.filter((item, index) => {
			return index + 1 <= this.paginationIndex * this.itemsPerPaginationSet - 1;
		});
	}

	protected get isPaginationComplete(): boolean {
		return this.shuffledProductPieces.length === this.paginatedProductPieces.length;
	}

	protected get productPiecesToShow(): IProductPiece[] {
		if (this.usePagination) return this.paginatedProductPieces;
		return this.shuffledProductPieces;
	}

	protected addNextPaginationSet(): void {
		if (!this.isPaginationComplete) {
			this.paginationIndex += 1;
		}
	}

	protected filterTaste(piece: IProductPiece): boolean {
		if (!piece.filters.taste.length) return false;

		const allDisabled = this.filterState.taste.length < 1;

		if (allDisabled) return true;

		return piece.filters.taste.some((taste: ProductPieceTaste): boolean => {
			return this.filterState.taste.includes(taste);
		});
	}

	protected filterConsistency(piece: IProductPiece): boolean {
		if (!piece.filters.consistency.length) return false;

		if (this.filterState.consistency === 'all') return true;

		return piece.filters.consistency.some((consistency: ProductPieceConsistency): boolean => {
			return this.filterState.consistency === consistency;
		});
	}

	protected filterElasticity(piece: IProductPiece): boolean {
		if (this.filterState.elasticity === null) return true;
		return (
			(piece.filters.elasticity === 'elastic' && this.filterState.elasticity) ||
			(piece.filters.elasticity === 'nonElastic' && !this.filterState.elasticity)
		);
	}

	protected filterVolume(piece: IProductPiece): boolean {
		if (this.filterState.volume === null) return true;
		return (
			(piece.filters.volume === 'loud' && this.filterState.volume) ||
			(piece.filters.volume === 'silent' && !this.filterState.volume)
		);
	}

	protected filterColor(piece: IProductPiece): boolean {
		if (!piece.filters.color.length) return false;

		const allDisabled = this.filterState.color.length < 1;

		if (allDisabled) return true;

		return piece.filters.color.some((color: NamedColors): boolean => {
			return this.filterState.color.includes(color);
		});
	}

	protected revealResults(): void {
		const results = this.$refs.results as HTMLElement;
		const filters = this.$refs.filters as HTMLElement;
		const resultsBounds: ClientRect = results.getBoundingClientRect();
		const filterHeight = filters.getBoundingClientRect().height;
		const resultElementSelector = '.m-inspiration-machine__result-item';
		const errorElementSelector = '.m-inspiration-machine__error-teaser';
		const hasExistingErrorResult = !!this.$el.querySelector(errorElementSelector);
		// we only need to stagger items above fold
		const staggerItemNum = 30;

		this.paginationIndex = 1;

		gsap.getTweensOf([resultElementSelector, errorElementSelector]).forEach(tween => {
			tween.progress(1).kill();
		});

		// Scroll into viewport if outside
		if (resultsBounds.top <= 20 || resultsBounds.top >= window.innerHeight - filterHeight) {
			this.LayoutUtil.disableNavigationAndExecuteCallback(() => {
				results.scrollIntoView();
			});
		}

		this.$nextTick(() => {
			gsap.from(`${resultElementSelector}:nth-child(-n+${staggerItemNum})`, {
				scale: 0.9,
				opacity: 0,
				duration: 0.2,
				delay: 0.1,
				stagger: {
					each: 0.07,
					grid: 'auto',
					from: 'start'
				},
				clearProps: true
			});

			if (!hasExistingErrorResult) {
				gsap.from(errorElementSelector, {
					opacity: 0,
					duration: 0.4,
					clearProps: true
				});
			}
		});
	}

	protected generateQueryStringFromFilters(): string {
		let queryString = '?';

		if (this.possibleFilters.taste.length) {
			queryString += `taste=${Object.values(this.filterState.taste)
				.filter(value => value)
				.join(',')}`;
		}

		if (this.possibleFilters.consistency.length) {
			queryString += `&consistency=${this.filterState.consistency}`;
		}

		if (this.possibleFilters.elasticity) {
			queryString += `&elasticity=${this.filterState.elasticity ? '1' : '0'}`;
		}

		if (this.possibleFilters.volume) {
			queryString += `&volume=${this.filterState.volume ? '1' : '0'}`;
		}

		if (this.possibleFilters.color.length) {
			queryString += `&color=${Object.values(this.filterState.color)
				.filter(value => value)
				.join(',')}`;
		}

		return queryString;
	}

	protected generateFiltersFromQueryString(qs: string): any {
		const urlParams = new URLSearchParams(qs);

		const obj: any = {};

		if (urlParams.has('taste')) {
			const value = urlParams.get('taste') as string;
			obj.taste = value ? [...value.split(',')] : [];
		}

		if (urlParams.has('consistency')) {
			obj.consistency = urlParams.get('consistency');
		}

		if (urlParams.has('elasticity')) {
			obj.elasticity = urlParams.get('elasticity') === '1';
		}

		if (urlParams.has('volume')) {
			obj.volume = urlParams.get('volume') === '1';
		}

		if (urlParams.has('color')) {
			const value = urlParams.get('color') as string;
			obj.color = value ? [...value.split(',')] : [];
		}

		return this.matchWithPossibleFilters({
			...this.activeFilters,
			...obj
		});
	}

	protected get consistencyOptions() {
		if (!this.possibleFilters.consistency.length) {
			return null;
		}

		return [
			{ label: this.i18n.all, value: 'all' },
			...this.possibleFilters.consistency.map(value => ({ value, label: this.i18n[value] }))
		];
	}

	protected teaserClick(url: string): void {
		const urlInstance = new URL(url);
		const qs = urlInstance.search;

		if (qs) {
			this.applyFiltersFromQueryString(qs);
		}
	}

	protected cycleTeasers(): void {
		const surpriseCount = this.shuffledSurpriseTeaser.length;
		const errorCount = this.shuffledErrorTeaser.length;

		this.surpriseTeaserIndex = this.surpriseTeaserIndex + 1 >= surpriseCount ? 0 : (this.surpriseTeaserIndex += 1);
		this.errorTeaserIndex = this.errorTeaserIndex + 1 >= errorCount ? 0 : (this.errorTeaserIndex += 1);
	}

	// ---------------------------------------------------------------------- overlay related

	protected get currentPiece() {
		if (!this.currentPieceId) return null;

		return this.productPieces.find(piece => {
			return piece.id === this.currentPieceId;
		});
	}

	protected get relatedProducts() {
		if (!this.currentPiece) return null;

		const ids = this.currentPiece.relatedProducts;

		return this.products.filter(product => {
			return ids.includes(product.id);
		});
	}

	protected async openOverlay(id: string): Promise<void> {
		await this.layoutUtilPromise;
		await this.focusLockPromise;

		this.openerElement = document.activeElement;
		this.currentPieceId = id;

		this.LayoutUtil.disableNavigationAndExecuteCallback((): void => {
			this.doubleRequestAnimationFrame(() => {
				const overlay = (this.$refs.overlay as Vue).$el as HTMLElement;
				this.LayoutUtil.deactivateMainContainer();
				this.FocusLock.on(overlay);
			});
		});
	}

	protected onOverlayClose(): void {
		this.currentPieceId = null;
	}

	protected afterOverlayLeaveAnimation(): void {
		this.LayoutUtil.activateMainContainer();

		if (this.openerElement) {
			(this.openerElement as HTMLElement).focus();
			this.openerElement = null;
		}
	}

	// ---------------------------------------------------------------------- breakpoint related

	private async initFilterSlider(): Promise<void> {
		const filterSliderOptions: IBaseSliderOptions = {
			flickityOptions: {
				groupCells: true,
				cellAlign: 'left',
				pageDots: true,
				contain: true,
				wrapAround: false,
				watchCSS: true
			}
		};

		await this.baseSliderPromise;
		this.filterSlider = new this.BaseSlider(this.$refs.filters as HTMLElement, filterSliderOptions);
	}

	// TODO: put it in util or at least import breakpoint definitions
	protected initMql() {
		const S = window.matchMedia('screen and (max-width: 620px)');
		const M = window.matchMedia('screen and (min-width: 621px) and (max-width: 980px)');
		const L = window.matchMedia('screen and (min-width: 981px) and (max-width: 1440px)');
		const XL = window.matchMedia('screen and (min-width: 1441px)');

		const checkMediaS = (): void => {
			if (S.matches) {
				this.itemsPerPaginationSet = 6;
				this.paginationIndex = 1;
			}
		};

		const checkMediaM = (): void => {
			if (M.matches) {
				this.itemsPerPaginationSet = 9;
				this.paginationIndex = 1;
			}
		};

		const checkMediaL = (): void => {
			if (L.matches) {
				this.itemsPerPaginationSet = 12;
				this.paginationIndex = 1;
			}
		};

		const checkMediaXL = (): void => {
			if (XL.matches) {
				this.itemsPerPaginationSet = 15;
				this.paginationIndex = 1;
			}
		};

		checkMediaS();
		checkMediaM();
		checkMediaL();
		checkMediaXL();
		S.addListener(checkMediaS);
		M.addListener(checkMediaM);
		L.addListener(checkMediaL);
		XL.addListener(checkMediaXL);
	}

	protected async initObserver(): Promise<void> {
		if (!('IntersectionObserver' in window)) {
			await import('intersection-observer');
		}

		const options = { threshold: 1 };

		const callback = (entries: IntersectionObserverEntry[]) => {
			entries.forEach((entry: IntersectionObserverEntry) => {
				this.hasResultsInViewport = entry.boundingClientRect.bottom < (entry.rootBounds as ClientRect).height;

				const untilL = window.matchMedia('screen and (max-width: 980px)');
				const fromL = window.matchMedia('screen and (min-width: 981px)');

				if (!this.sliderIntroFinished && this.hasResultsInViewport && !this.sliderIntroAnimation && untilL.matches) {
					this.sliderIntroAnimation = this.filterSlideIntroAnimation();
				} else if (!this.sliderIntroFinished && this.hasResultsInViewport && fromL.matches) {
					this.sliderIntroFinished = true;
					this.initFilterSlider();
				}
			});
		};

		const resultContainerObserver = new IntersectionObserver(callback, options);

		resultContainerObserver.observe(this.$refs.sentinel as HTMLElement);
	}

	protected filterSlideIntroAnimation(): GSAPTimeline {
		const filterBlocks: HTMLElement[] = Array.from(
			(this.$refs.filters as HTMLElement).querySelectorAll('.m-inspiration-machine__filter-block')
		);
		const self = this;

		const containerWidth = filterBlocks.reduce((acc, item) => item.getBoundingClientRect().width + acc, 0);

		const tl: GSAPTimeline = gsap.timeline();

		tl.from('.m-inspiration-machine__filters', { delay: 0.5, duration: 0.5, height: 0, clearProps: true }).from(
			'.m-inspiration-machine__filters-inner',
			{
				duration: 2,
				delay: 0.2,
				x: (containerWidth + 50) * -1,
				clearProps: true,
				onComplete() {
					self.sliderIntroFinished = true;
					self.initFilterSlider();
				}
			}
		);

		return tl;
	}

	// ---------------------------------------------------------------------- utils (maybe put in own file)

	/**
	 * Wait for a browser repaint. Supposed to be a better solution as setTimeout.
	 * https://github.com/vuejs/vue/issues/9200#issuecomment-468512304
	 */
	private doubleRequestAnimationFrame = (callback: () => void): number => {
		return requestAnimationFrame(() => {
			requestAnimationFrame(callback);
		});
	};

	protected shuffleArray(arr: any[]): any[] {
		return arr
			.map(a => [Math.random(), a])
			.sort((a, b) => (a[0] as number) - (b[0] as number))
			.map(a => a[1]) as any[];
	}
}

interface IInspirationMachineTeaser {
	headline?: string;
	link: ILink;
}
